package com.schoolconnect.app.state

import com.schoolconnect.app.datasource.network.AuthSessionStore
import com.schoolconnect.app.datasource.network.UserSessionStore
import com.schoolconnect.app.datasource.NavigationStateStore
import com.schoolconnect.app.domain.SchoolKeys
import com.schoolconnect.app.feature.home.HomeDefaults
import com.schoolconnect.app.feature.home.EditProfileFormValues
import com.schoolconnect.app.feature.common.ShellProfileStore
import com.schoolconnect.app.feature.common.ParentChildSelectionStore
import com.schoolconnect.app.repository.profile.ProfileRepository
import com.schoolconnect.app.designsystem.AppToastCenter
import com.schoolconnect.app.feature.rolepicker.RolePickerUiState
import com.schoolconnect.app.navigation.FeatureRoute
import com.schoolconnect.app.navigation.HomeRoute
import com.schoolconnect.app.repository.role.RoleLookupRepository
import com.schoolconnect.app.repository.systemlookup.SystemLookupRepository
import com.schoolconnect.app.repository.common.TenantContextCacheCoordinator
import com.schoolconnect.app.repository.auth.AuthRepository
import com.schoolconnect.app.repository.home.HomeDashboardRepository
import com.schoolconnect.app.repository.analytics.SchoolStatisticsRepository
import com.schoolconnect.app.repository.analytics.SchoolAnalyticsRepository
import com.schoolconnect.app.repository.user.UserRepository
import com.schoolconnect.app.repository.school.SchoolRepository
import com.schoolconnect.app.util.scLog
import kotlin.time.Clock
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.Job
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.cancel
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update

class AppViewModel {
    private companion object {
        const val TAG = "[DATA_RETRIEVE_SC] AppViewModel"
        const val MIN_SPLASH_DURATION_MS = 3_000L
    }

    private val scope = CoroutineScope(SupervisorJob() + Dispatchers.Default)
    private val _uiState = MutableStateFlow(AppUiState())
    private var bootstrapStarted = false
    private var bootstrapStartedAtMs: Long = 0L
    private var bootstrapJob: Job? = null
    private var bootstrapGeneration: Long = 0L

    val uiState: StateFlow<AppUiState> = _uiState.asStateFlow()

    fun bootstrapSession() {
        if (bootstrapStarted) return
        bootstrapStarted = true
        bootstrapStartedAtMs = Clock.System.now().toEpochMilliseconds()
        scLog("$TAG bootstrapSession start")
        _uiState.update {
            it.copy(
                isBootstrapping = true,
                currentRoute = FeatureRoute("splash")
            )
        }
        val generation = ++bootstrapGeneration
        bootstrapJob?.cancel()
        bootstrapJob = scope.launch {
            // Lookup-backed forms convert display labels to protocol codes.
            // Complete this shared warm-up before exposing authenticated
            // screens so a fast click cannot observe an empty lookup cache.
            warmSystemLookups()
            bootstrapSessionInternal(generation)
        }
    }

    fun toggleTheme() {
        _uiState.update { it.copy(isDarkTheme = !it.isDarkTheme) }
    }

    fun saveCurrentProfile(values: EditProfileFormValues) {
        val fullName = values.fullName.trim().ifBlank {
            listOf(values.firstName, values.middleName, values.lastName)
                .map(String::trim)
                .filter(String::isNotBlank)
                .joinToString(" ")
        }
        scope.launch {
            ProfileRepository.updateCurrentProfile(
                _uiState.value.homeRoleCode,
                ProfileRepository.ProfileUpdate(
                    fullName = fullName,
                    pushNotificationsEnabled = values.pushNotificationsEnabled,
                    emailNotificationsEnabled = values.emailNotificationsEnabled,
                    photoFileId = values.selectedPhotoFileId
                )
            ).onSuccess {
                AppToastCenter.showSuccess("Profile updated successfully")
                refreshShellProfile()
            }
                .onFailure { throwable -> scLog("$TAG saveCurrentProfile failure", throwable) }
        }
    }

    private fun refreshShellProfile() {
        scope.launch {
            val result = when (_uiState.value.homeRoleCode) {
                SchoolKeys.V_ROLE_PARENT -> ProfileRepository.loadParentProfile().map { profile ->
                    ShellProfileStore.update(
                        fullName = profile.name.orEmpty(),
                        email = profile.email.orEmpty(),
                        profileImageUrl = profile.profileImageUrl,
                        pushNotificationsEnabled = profile.pushNotificationEnabled,
                        emailNotificationsEnabled = profile.emailNotificationEnabled,
                        mobileNumber = profile.mobile,
                        employeeId = "",
                        schoolRole = "",
                        authRole = ""
                    )
                }
                SchoolKeys.V_ROLE_SCHOOL_ADMIN,
                SchoolKeys.V_ROLE_TEACHER,
                SchoolKeys.V_ROLE_SUPPORT_STAFF -> ProfileRepository.loadSchoolStaffProfile().map { profile ->
                    ShellProfileStore.update(
                        fullName = profile.name.orEmpty(),
                        email = profile.email.orEmpty(),
                        profileImageUrl = profile.profileImageUrl,
                        pushNotificationsEnabled = profile.pushNotificationEnabled,
                        emailNotificationsEnabled = profile.emailNotificationEnabled,
                        mobileNumber = profile.resolvedMobile,
                        employeeId = profile.employeeId.orEmpty(),
                        schoolRole = profile.roleTitle.orEmpty(),
                        authRole = profile.authRole.orEmpty()
                    )
                }
                else -> ProfileRepository.loadAppStaffProfile().map { profile ->
                    ShellProfileStore.update(
                        fullName = profile.name.orEmpty(),
                        email = profile.email.orEmpty(),
                        profileImageUrl = profile.profileImageUrl,
                        pushNotificationsEnabled = profile.pushNotificationEnabled,
                        emailNotificationsEnabled = profile.emailNotificationEnabled,
                        mobileNumber = profile.mobile,
                        employeeId = profile.employeeId.orEmpty(),
                        schoolRole = "",
                        authRole = ""
                    )
                }
            }
            result.onFailure { throwable -> scLog("$TAG refreshShellProfile failure", throwable) }
        }
    }

    fun openHome() {
        persistRailSelection("dashboard", _uiState.value.homeRoleCode, _uiState.value.homeRoleSchoolId)
        _uiState.update { it.copy(currentRoute = HomeRoute) }
    }

    fun openFeature(featureId: String) {
        val normalizedFeatureId = featureId.trim().ifBlank { "dashboard" }
        persistRailSelection(
            normalizedFeatureId,
            _uiState.value.homeRoleCode,
            _uiState.value.homeRoleSchoolId
        )
        _uiState.update {
            it.copy(
                currentRoute = FeatureRoute(normalizedFeatureId),
                restoredRailId = normalizedFeatureId
            )
        }
    }

    fun updateHomeRole(
        roleCode: String,
        schoolId: String,
        schoolLabel: String = ""
    ) {
        val normalizedRoleCode = roleCode.trim()
        val normalizedSchoolId = schoolId.trim()
        val normalizedSchoolLabel = schoolLabel.trim()
        val normalizedAssignmentId = UserSessionStore.getActiveAssignmentId().trim()
        val resolvedSchoolLabel = normalizedSchoolLabel.resolveSchoolLabelCandidate(normalizedSchoolId)
        scLog(
            "$TAG updateHomeRole role=$normalizedRoleCode school=${normalizedSchoolId.ifBlank { "<blank>" }}"
        )
        // Drop previous role's staff metadata until the new /me profile loads.
        ShellProfileStore.clearStaffFields()
        TenantContextCacheCoordinator.invalidateAll("home-context-update")
        UserSessionStore.setHomeRoleCode(normalizedRoleCode)
        UserSessionStore.setHomeRoleSchoolId(normalizedSchoolId)
        UserSessionStore.setActiveAssignmentId(normalizedAssignmentId)
        val currentSchoolCode = UserSessionStore.getHomeRoleSchoolCode().trim()
        val nextRailId = restoredRailId(
            userId = UserSessionStore.getUserId(),
            roleCode = normalizedRoleCode,
            schoolId = normalizedSchoolId
        )
        if (currentSchoolCode.isBlank() && normalizedSchoolId.isNotBlank()) {
            scope.launch {
                resolveAndStoreHomeSchoolCode(normalizedSchoolId, currentSchoolCode)
            }
        }
        _uiState.update {
            it.copy(
                homeRoleCode = normalizedRoleCode,
                homeRoleSchoolId = normalizedSchoolId,
                homeRoleSchoolLabel = resolvedSchoolLabel,
                homeRoleSchoolCode = currentSchoolCode,
                activeAssignmentId = normalizedAssignmentId,
                restoredRailId = nextRailId,
                currentRoute = routeForRail(normalizedRoleCode, nextRailId)
            )
        }
        resolveAndStoreHomeSchoolLabelIfNeeded(normalizedSchoolId, resolvedSchoolLabel)
    }

    fun logout() {
        scLog("$TAG logout")
        if (_uiState.value.isLoggingOut) return
        _uiState.update { it.copy(isLoggingOut = true, feedbackMessage = "", feedbackNonce = it.feedbackNonce) }
        scope.launch {
            val result = AuthRepository.logout()
            result.onFailure { throwable ->
                scLog("$TAG logout request failed", throwable)
            }

            UserSessionStore.clear()
            ParentChildSelectionStore.clear()
            ShellProfileStore.clear()
            clearSessionCaches()
            invalidateBootstrap()
            bootstrapStarted = false
            _uiState.update {
                it.copy(
                    isAuthenticated = false,
                    isBootstrapping = false,
                    isLoggingOut = false,
                    currentRoute = FeatureRoute("auth"),
                    currentUserId = "",
                    activeAssignmentId = "",
                    homeRoleCode = "",
                    homeRoleSchoolId = "",
                    homeRoleSchoolLabel = "",
                    homeRoleSchoolCode = "",
                    restoredRailId = "dashboard",
                    startupRolePicker = RolePickerUiState(),
                    feedbackMessage = result.fold(
                        onSuccess = { "Session terminated successfully. You may now close this window or navigate to login." },
                        onFailure = { "Session cleared locally after logout request failed." }
                    ),
                    feedbackNonce = it.feedbackNonce + 1
                )
            }
        }
    }

    fun forceLogin(message: String = "Your session expired. Please sign in again.") {
        scLog("$TAG forceLogin reason=session_expired")
        UserSessionStore.clear()
        ParentChildSelectionStore.clear()
        ShellProfileStore.clear()
        clearSessionCaches()
        invalidateBootstrap()
        bootstrapStarted = false
        _uiState.update {
            it.copy(
                isAuthenticated = false,
                isBootstrapping = false,
                isLoggingOut = false,
                currentRoute = FeatureRoute("auth"),
                currentUserId = "",
                activeAssignmentId = "",
                homeRoleCode = "",
                homeRoleSchoolId = "",
                homeRoleSchoolLabel = "",
                homeRoleSchoolCode = "",
                restoredRailId = "dashboard",
                startupRolePicker = RolePickerUiState(),
                feedbackMessage = message,
                feedbackNonce = it.feedbackNonce + 1
            )
        }
    }

    fun completeAuthentication(
        userId: String,
        roleCode: String,
        schoolId: String,
        schoolLabel: String = ""
    ) {
        val assignmentId = UserSessionStore.getActiveAssignmentId().trim()
        if (assignmentId.isBlank()) {
            applyAuthenticatedState(userId, roleCode, schoolId, schoolLabel)
            return
        }

        scope.launch {
            UserRepository.updateUserRoleSelection(assignmentId).fold(
                onSuccess = {
                    applyAuthenticatedState(userId, roleCode, schoolId, schoolLabel)
                    scLog("$TAG completeAuthentication selected assignmentId=$assignmentId")
                },
                onFailure = { throwable ->
                    scLog("$TAG completeAuthentication context selection failed assignmentId=$assignmentId", throwable)
                    _uiState.update {
                        it.copy(
                            feedbackMessage = "Unable to establish the selected workspace context. Please retry login.",
                            feedbackNonce = it.feedbackNonce + 1
                        )
                    }
                }
            )
        }
    }

    private fun applyAuthenticatedState(
        userId: String,
        roleCode: String,
        schoolId: String,
        schoolLabel: String = ""
    ) {
        val normalizedUserId = userId.trim()
        val normalizedRoleCode = roleCode.trim()
        val normalizedSchoolId = schoolId.trim()
        val normalizedSchoolLabel = schoolLabel.trim()
        val normalizedAssignmentId = UserSessionStore.getActiveAssignmentId().trim()
        val resolvedSchoolLabel = normalizedSchoolLabel.resolveSchoolLabelCandidate(normalizedSchoolId)
        val currentSchoolCode = UserSessionStore.getHomeRoleSchoolCode().trim()
        val nextRailId = restoredRailId(normalizedUserId, normalizedRoleCode, normalizedSchoolId)
        scLog("$TAG completeAuthentication user=$normalizedUserId role=$normalizedRoleCode school=$normalizedSchoolId")
        UserSessionStore.setUserId(normalizedUserId)
        UserSessionStore.setHomeRoleCode(normalizedRoleCode)
        UserSessionStore.setHomeRoleSchoolId(normalizedSchoolId)
        UserSessionStore.setActiveAssignmentId(normalizedAssignmentId)
        if (currentSchoolCode.isBlank() && normalizedSchoolId.isNotBlank()) {
            scope.launch {
                resolveAndStoreHomeSchoolCode(normalizedSchoolId, currentSchoolCode)
            }
        }
        warmRoleCatalog(normalizedSchoolId.ifBlank { null })
        _uiState.update {
            it.copy(
                isAuthenticated = true,
                isBootstrapping = false,
                isLoggingOut = false,
                currentRoute = routeForRail(normalizedRoleCode, nextRailId),
                currentUserId = normalizedUserId,
                activeAssignmentId = normalizedAssignmentId,
                homeRoleCode = normalizedRoleCode,
                homeRoleSchoolId = normalizedSchoolId,
                homeRoleSchoolLabel = resolvedSchoolLabel,
                homeRoleSchoolCode = currentSchoolCode,
                restoredRailId = nextRailId,
                feedbackMessage = "",
                startupRolePicker = RolePickerUiState()
            )
        }
        // Load the shared shell profile independently of the active route. The
        // authenticated app can open a standalone feature without creating a
        // HomeViewModel, but the global My Profile dialog still depends on
        // ShellProfileStore.
        refreshShellProfile()
        resolveAndStoreHomeSchoolLabelIfNeeded(normalizedSchoolId, resolvedSchoolLabel)
    }

    private suspend fun bootstrapSessionInternal(generation: Long) {
        val savedUserId = UserSessionStore.getUserId().trim()
        val savedAssignmentId = UserSessionStore.getActiveAssignmentId().trim()
        val savedRoleCode = UserSessionStore.getHomeRoleCode().trim()
        val savedSchoolId = UserSessionStore.getHomeRoleSchoolId().trim()
        val savedSchoolCode = UserSessionStore.getHomeRoleSchoolCode().trim()
        val savedAuthSession = AuthSessionStore.getSession()
        val hasStoredAuthProfiles = savedAuthSession
            ?.response
            ?.identityContextProfiles
            ?.isNotEmpty() == true
        val authContext = AuthSessionStore.primaryContext()
        val resolvedAssignmentId = savedAssignmentId.ifBlank { authContext?.assignmentId.orEmpty() }
        val hasSingleAuthProfile = savedAuthSession
            ?.response
            ?.identityContextProfiles
            ?.size == 1
        val resolvedUserId = savedUserId.ifBlank { authContext?.userId.orEmpty() }
        // For a single-profile login, the signed-in auth profile is the source
        // of truth. This also repairs stale persisted role codes from older
        // role-id mappings (for example, Teacher previously being stored as
        // Parent). Multi-profile users retain their explicit selection.
        val resolvedRoleCode = if (savedAuthSession != null && !hasStoredAuthProfiles) {
            ""
        } else if (hasSingleAuthProfile) {
            authContext?.roleCode.orEmpty().ifBlank { savedRoleCode }
        } else {
            savedRoleCode.ifBlank { authContext?.roleCode.orEmpty() }
        }
        val resolvedSchoolId = if (hasSingleAuthProfile) {
            authContext?.schoolId.orEmpty().ifBlank { savedSchoolId }
        } else {
            savedSchoolId.ifBlank { authContext?.schoolId.orEmpty() }
        }
        // A school code is optional session enrichment. Do not resolve it from
        // the platform-wide schools list during bootstrap: tenant-scoped roles
        // such as Teacher are not authorized to read that endpoint. The home
        // flow can resolve it later only when a school-admin capability needs it.
        val resolvedSchoolCode = savedSchoolCode
        val resolvedSchoolLabel = resolveSchoolLabel(
            schoolId = resolvedSchoolId,
            preferredLabel = authContext?.schoolLabel.orEmpty()
        )
        val resolvedRailId = restoredRailId(resolvedUserId, resolvedRoleCode, resolvedSchoolId)
        if (resolvedSchoolCode.isNotBlank()) {
            UserSessionStore.setHomeRoleSchoolCode(resolvedSchoolCode)
        }
        if (hasSingleAuthProfile && resolvedRoleCode != savedRoleCode) {
            UserSessionStore.setHomeRoleCode(resolvedRoleCode)
        }
        if (hasSingleAuthProfile && resolvedSchoolId != savedSchoolId) {
            UserSessionStore.setHomeRoleSchoolId(resolvedSchoolId)
        }
        if (resolvedAssignmentId != savedAssignmentId) {
            UserSessionStore.setActiveAssignmentId(resolvedAssignmentId)
        }
        scLog("$TAG bootstrapSessionInternal savedUserId=${savedUserId.ifBlank { "<blank>" }}")
        if (resolvedUserId.isBlank() || resolvedRoleCode.isBlank()) {
            if (savedAuthSession != null && !hasStoredAuthProfiles) {
                UserSessionStore.clear()
                scLog("$TAG bootstrap cleared invalid saved session reason=no_role_profiles")
            }
            finishBootstrap {
                if (!isCurrentBootstrap(generation)) return@finishBootstrap
                _uiState.update {
                    it.copy(
                        isAuthenticated = false,
                        isBootstrapping = false,
                        isLoggingOut = false,
                        currentRoute = FeatureRoute("auth"),
                        currentUserId = resolvedUserId,
                        activeAssignmentId = "",
                        homeRoleCode = "",
                        homeRoleSchoolId = "",
                        homeRoleSchoolLabel = "",
                        homeRoleSchoolCode = "",
                        restoredRailId = "dashboard",
                        feedbackMessage = "",
                        startupRolePicker = RolePickerUiState()
                    )
                }
            }
            scLog("$TAG bootstrap complete reason=no_local_session")
            return
        }

        if (resolvedAssignmentId.isNotBlank()) {
            UserRepository.updateUserRoleSelection(resolvedAssignmentId).onFailure { throwable ->
                scLog(
                    "$TAG bootstrap context selection failed assignmentId=$resolvedAssignmentId",
                    throwable
                )
            }
        }
        warmRoleCatalog(resolvedSchoolId.ifBlank { null })
        finishBootstrap {
            if (!isCurrentBootstrap(generation)) return@finishBootstrap
            _uiState.update {
                it.copy(
                    isAuthenticated = true,
                    isBootstrapping = false,
                    isLoggingOut = false,
                    currentRoute = routeForRail(resolvedRoleCode, resolvedRailId),
                    currentUserId = resolvedUserId,
                    activeAssignmentId = resolvedAssignmentId,
                    homeRoleCode = resolvedRoleCode.ifBlank { SchoolKeys.V_ROLE_APP_ADMIN },
                    homeRoleSchoolId = resolvedSchoolId,
                    homeRoleSchoolLabel = resolvedSchoolLabel,
                    homeRoleSchoolCode = resolvedSchoolCode,
                    restoredRailId = resolvedRailId,
                    feedbackMessage = "",
                    startupRolePicker = RolePickerUiState()
                )
            }
            // Populate the profile store before/while standalone routes render.
            refreshShellProfile()
        }

        scLog("$TAG bootstrap complete user=$resolvedUserId role=$resolvedRoleCode school=${resolvedSchoolId.ifBlank { "<blank>" }}")
    }

    private fun restoredRailId(userId: String, roleCode: String, schoolId: String): String {
        val normalizedRoleCode = roleCode.trim()
        if (userId.trim().isBlank() || normalizedRoleCode.isBlank()) return "dashboard"
        val savedRailId = NavigationStateStore.getLastRailId(userId, normalizedRoleCode, schoolId)
        return savedRailId.takeIf { it in HomeDefaults.allowedRailIds(normalizedRoleCode) } ?: "dashboard"
    }

    private fun persistRailSelection(railId: String, roleCode: String, schoolId: String) {
        val userId = _uiState.value.currentUserId.ifBlank { UserSessionStore.getUserId() }.trim()
        val normalizedRoleCode = roleCode.trim()
        if (userId.isBlank() || normalizedRoleCode.isBlank()) return
        if (railId !in HomeDefaults.allowedRailIds(normalizedRoleCode)) return
        NavigationStateStore.setLastRailId(userId, normalizedRoleCode, schoolId, railId)
    }

    private fun routeForRail(roleCode: String, railId: String): Any {
        val normalizedRailId = railId.trim().ifBlank { "dashboard" }
        return if (
            normalizedRailId == "config" ||
            (roleCode.trim() == SchoolKeys.V_ROLE_TEACHER && normalizedRailId == "students")
        ) {
            FeatureRoute(normalizedRailId)
        } else {
            HomeRoute
        }
    }

    private suspend fun resolveSchoolCode(schoolId: String): String {
        return SchoolRepository.resolveSchoolCodeById(schoolId).getOrElse { throw it }
    }

    private suspend fun resolveSchoolLabel(schoolId: String, preferredLabel: String = ""): String {
        val normalizedSchoolId = schoolId.trim()
        val normalizedPreferredLabel = preferredLabel.trim()
        if (normalizedPreferredLabel.isSchoolLabelCandidate(normalizedSchoolId)) {
            return normalizedPreferredLabel
        }
        if (normalizedSchoolId.isBlank()) {
            return normalizedPreferredLabel.ifBlank { "iConnect.School" }
        }
        // School-scoped roles must not resolve labels through the platform-wide
        // schools endpoint. Their auth context already contains the tenant label.
        if (currentRoleIsSchoolScoped()) {
            return normalizedPreferredLabel.ifBlank { "School Context" }
        }
        return SchoolRepository.resolveSchoolNameById(normalizedSchoolId).getOrElse {
            normalizedPreferredLabel.ifBlank { "School Context" }
        }
    }

    private suspend fun resolveAndStoreHomeSchoolCode(schoolId: String, fallbackCode: String = "") {
        if (schoolId.isBlank()) return
        if (currentRoleIsSchoolScoped()) {
            scLog("$TAG resolveAndStoreHomeSchoolCode skipped tenant role schoolId=$schoolId")
            return
        }
        val resolved = runCatching { resolveSchoolCode(schoolId) }
            .onFailure { throwable ->
                scLog(
                    "$TAG resolveAndStoreHomeSchoolCode failure schoolId=$schoolId; keeping existing context",
                    throwable
                )
            }
            .getOrNull()
            .orEmpty()
        if (resolved.isBlank()) {
            scLog("$TAG resolveAndStoreHomeSchoolCode failed schoolId=$schoolId")
            return
        }
        UserSessionStore.setHomeRoleSchoolCode(resolved)
        _uiState.update { current ->
            if (current.homeRoleSchoolCode.isNotBlank() && current.homeRoleSchoolCode != fallbackCode) {
                current
            } else {
                current.copy(homeRoleSchoolCode = resolved)
            }
        }
        scLog("$TAG resolveAndStoreHomeSchoolCode complete schoolId=$schoolId schoolCode=$resolved")
    }

    private fun currentRoleIsSchoolScoped(): Boolean {
        val roleCode = UserSessionStore.getHomeRoleCode().ifBlank {
            AuthSessionStore.primaryContext()?.roleCode.orEmpty()
        }
        return roleCode in setOf(
            SchoolKeys.V_ROLE_SCHOOL_ADMIN,
            SchoolKeys.V_ROLE_TEACHER,
            SchoolKeys.V_ROLE_PARENT,
            SchoolKeys.V_ROLE_SUPPORT_STAFF
        )
    }

    private fun resolveAndStoreHomeSchoolLabelIfNeeded(schoolId: String, currentLabel: String) {
        if (schoolId.isBlank()) return
        if (currentLabel.isSchoolLabelCandidate(schoolId)) return
        scope.launch {
            scLog("$TAG resolveAndStoreHomeSchoolLabel start schoolId=$schoolId")
            val resolved = resolveSchoolLabel(schoolId, currentLabel)
            if (resolved.isBlank() || resolved == currentLabel) {
                scLog("$TAG resolveAndStoreHomeSchoolLabel skipped schoolId=$schoolId label=${resolved.ifBlank { "<blank>" }}")
                return@launch
            }
            _uiState.update { current ->
                current.copy(homeRoleSchoolLabel = resolved)
            }
            scLog("$TAG resolveAndStoreHomeSchoolLabel complete schoolId=$schoolId schoolLabel=$resolved")
        }
    }

    private fun String.isSchoolLabelCandidate(schoolId: String): Boolean {
        val normalized = trim()
        if (normalized.isBlank()) return false
        if (normalized == schoolId.trim()) return false
        if (normalized.looksLikeUuid()) return false
        return true
    }

    private fun String.resolveSchoolLabelCandidate(schoolId: String): String {
        val normalized = trim()
        if (normalized.isSchoolLabelCandidate(schoolId)) return normalized
        return if (schoolId.trim().isBlank()) "iConnect.School" else "School Context"
    }

    private fun String.looksLikeUuid(): Boolean {
        val normalized = trim()
        if (normalized.length != 36) return false
        return normalized[8] == '-' &&
            normalized[13] == '-' &&
            normalized[18] == '-' &&
            normalized[23] == '-' &&
            normalized.count { it == '-' } == 4
    }

    private fun warmRoleCatalog(schoolId: String? = null) {
        scope.launch {
            if (shouldSkipGlobalRoleCatalogWarmup()) {
                scLog("$TAG warmRoleCatalog skipped school=${schoolId ?: "<global>"} role=${UserSessionStore.getHomeRoleCode().trim()}")
                return@launch
            }
            scLog("$TAG warmRoleCatalog start school=${schoolId ?: "<global>"}")
            RoleLookupRepository.refreshRolesFromRemote(
                schoolId = schoolId,
                includeGlobal = true,
                forceRemote = true
            ).fold(
                onSuccess = { roles ->
                    scLog("$TAG warmRoleCatalog complete school=${schoolId ?: "<global>"} count=${roles.size}")
                },
                onFailure = { throwable ->
                    scLog("$TAG warmRoleCatalog failure school=${schoolId ?: "<global>"}", throwable)
                }
            )
        }
    }

    private suspend fun warmSystemLookups() {
        SystemLookupRepository.refresh(forceRemote = true).onFailure { throwable ->
            // Lookup metadata is generic and useful to every role, but a
            // transient metadata failure must not prevent the user from logging in.
            scLog("$TAG warmSystemLookups failure; continuing startup", throwable)
        }
    }

    private fun shouldSkipGlobalRoleCatalogWarmup(): Boolean {
        return UserSessionStore.getHomeRoleCode().trim() in setOf(
            SchoolKeys.V_ROLE_SCHOOL_ADMIN,
            SchoolKeys.V_ROLE_TEACHER,
            SchoolKeys.V_ROLE_PARENT
        )
    }

    fun selectStartupRole(optionId: String) {
        _uiState.update {
            it.copy(
                startupRolePicker = it.startupRolePicker.copy(selectedOptionId = optionId)
            )
        }
    }

    fun confirmStartupRoleSelection() {
        val state = _uiState.value
        val selectedOption = state.startupRolePicker.options.firstOrNull { it.id == state.startupRolePicker.selectedOptionId }
            ?: state.startupRolePicker.options.firstOrNull()
            ?: return

        scLog(
            "$TAG confirmStartupRoleSelection role=${selectedOption.roleCode} school=${selectedOption.schoolId.ifBlank { "<blank>" }}"
        )

        completeAuthentication(
            userId = state.currentUserId.ifBlank { UserSessionStore.getUserId() },
            roleCode = selectedOption.roleCode,
            schoolId = selectedOption.schoolId,
            schoolLabel = selectedOption.schoolLabel
        )
    }

    private suspend fun finishBootstrap(block: () -> Unit) {
        val elapsed = Clock.System.now().toEpochMilliseconds() - bootstrapStartedAtMs
        val remaining = MIN_SPLASH_DURATION_MS - elapsed
        if (remaining > 0) {
            delay(remaining)
        }
        block()
    }

    private fun isCurrentBootstrap(generation: Long): Boolean =
        generation == bootstrapGeneration

    private fun invalidateBootstrap() {
        bootstrapGeneration += 1
        bootstrapJob?.cancel()
        bootstrapJob = null
    }

    fun clear() {
        invalidateBootstrap()
        scope.cancel()
    }

    private fun clearSessionCaches() {
        TenantContextCacheCoordinator.invalidateAll("session-clear")
        RoleLookupRepository.clearMemoryCache()
        SystemLookupRepository.clear()
    }
}
